import { IDependentValues, IFormatterProps } from '../../@types/workspaceTypes';
import React, { MouseEventHandler, useContext, useMemo } from 'react';
import { BasicHeader } from '../../actions/BasicHeader';
import Row from '../connectedForm/Row';
import Fieldset from '../connectedForm/Fieldset';
import RowMoveCell from '../react-data-grid-addons/src/draggable/RowMoveCell';
import { mergeClasses, Popover, PopoverSurface, PopoverTrigger } from '@fluentui/react-components';
import { IPurchaseOrderLine, Maybe, ProductClass, PurchaseOrderLineStatus } from '../../graphql/schema/p4types';
import { DragTitle, STATUS_CELL_BASIC_HEADER_STYLES, StatusCircle, useStatusCellStyles } from './util';
import ConnectedFormContext from '../Context/ConnectedFormContext';
import { useVarianceQuantity } from './SalesOrderStatusCellFormatter';
import { useFullUserDetails } from '../../utils/hooks';

export const WARNING_STATUSES = [PurchaseOrderLineStatus.Assigned];

export const SUCCESS_STATUSES = [
  PurchaseOrderLineStatus.Received,
  PurchaseOrderLineStatus.Invoiced,
  PurchaseOrderLineStatus.ConsideredComplete,
];

const getOutstandingReceived = (dependentValues: IDependentValues<IPurchaseOrderLine>) => {
  const isFreeType = dependentValues?.rowData?.product?.productClass === ProductClass.FreeType;

  let quantity = 0;
  let received = 0;
  let outstanding = 0;

  if (!isFreeType) {
    quantity = dependentValues.rowData.quantity ?? 0;
    received = dependentValues.rowData.received ?? 0;
    outstanding = !dependentValues?.rowData?.considerComplete ? Math.max(quantity - received, 0) ?? 0 : 0;
  }

  return { outstanding, received };
};

const PurchaseOrderStatusDisplay = ({ dependentValues }: { dependentValues: IDependentValues<IPurchaseOrderLine> }) => {
  const title = useMemo(() => {
    const description = dependentValues.rowData.description;
    if (description) {
      return `Status Display - ${description}`;
    }
    return 'Status Display';
  }, [dependentValues.rowData.description]);

  const styles = useStatusCellStyles();

  const { outstanding, received } = getOutstandingReceived(dependentValues);

  return (
    <>
      <BasicHeader secondaryTitle={<DragTitle />} styles={STATUS_CELL_BASIC_HEADER_STYLES}>
        {title}
      </BasicHeader>
      <div style={{ padding: '1em', minWidth: '600px' }}>
        <Row>
          <Fieldset cols={1 / 3}>
            <StatusCircle message={`Awaiting Delivery: ${outstanding}`} iconClassName={styles.dangerCircle} />
          </Fieldset>
          <Fieldset cols={1 / 3}>
            <StatusCircle
              message={`Assigned: ${dependentValues.rowData.assigned ?? 0}`}
              iconClassName={styles.warningCircle}
            />
          </Fieldset>
          <Fieldset cols={1 / 3}>
            <StatusCircle message={`Received: ${received ?? 0}`} iconClassName={styles.successCircle} />
            <StatusCircle
              message={`Invoice: ${dependentValues.rowData.invoicedQuantity ?? 0}`}
              iconClassName={styles.successCircle}
            />
          </Fieldset>
        </Row>
      </div>
    </>
  );
};
export const PurchaseOrderStatusCellFormatter = (props: IFormatterProps<IPurchaseOrderLine>) => {
  const fullUserDetails = useFullUserDetails();
  const { dependentValues, rowIdx, idx, column } = props;
  const styles = useStatusCellStyles();
  const { values } = useContext(ConnectedFormContext);

  const localVarianceQuantity = useVarianceQuantity({ purchaseOrderLine: dependentValues.rowData });

  const onClick: MouseEventHandler<any> = (e) => {
    const meta = dependentValues.cellMetaData;
    if (meta?.onCellClick && typeof meta.onCellClick === 'function') {
      meta.onCellClick({ rowIdx: rowIdx, idx: idx }, e);
    }
  };

  //If status display is disabled, just show the regular tooltip (Handled by RowMoveCell).
  if (
    !fullUserDetails?.companyUser?.additionalStatusDisplays ||
    !dependentValues.rowData.product ||
    dependentValues.rowData.archived ||
    values.archived
  ) {
    return (
      <div
        className={'react-grid-Cell'}
        style={{ width: column.width, height: '36px', position: 'absolute' }}
        onClick={onClick}
      >
        <div className="react-grid-Cell__value" style={{ position: 'relative' }}>
          <RowMoveCell {...props} showTooltip={true} />
        </div>
      </div>
    );
  }

  const statuses: Array<Maybe<PurchaseOrderLineStatus>> = dependentValues.rowData?.purchaseOrderLineStatus ?? [];

  const { outstanding } = getOutstandingReceived(dependentValues);

  let cellClassName: string | undefined;
  if (-localVarianceQuantity < outstanding) {
    cellClassName = styles.cellDanger;
  } else if (statuses.some((status) => WARNING_STATUSES.includes(status!))) {
    cellClassName = styles.cellWarning;
  } else if (statuses.some((status) => SUCCESS_STATUSES.includes(status!))) {
    cellClassName = styles.cellSuccess;
  }

  return (
    <Popover openOnHover={true} positioning={'after-bottom'} mouseLeaveDelay={0}>
      <PopoverTrigger>
        <div
          className={mergeClasses(styles.cell, cellClassName)}
          style={{ width: column.width, height: 36 }}
          onClick={onClick}
        >
          <div className="react-grid-Cell__value" style={{ position: 'relative' }}>
            <RowMoveCell {...props} showTooltip={false} />
          </div>
        </div>
      </PopoverTrigger>
      <PopoverSurface className={styles.surface}>
        <PurchaseOrderStatusDisplay dependentValues={props.dependentValues} />
      </PopoverSurface>
    </Popover>
  );
};
